import { useAuthStoreShallow } from "@/features/authentication";
import { VERSIONS_TABLE_SESSIONS_SIZES } from "@pages/analytics/constants";
import type { ReaderType } from "@pages/analytics/types";
import { ReaderMetaInfo } from "@pages/analytics/components/reader-meta-info";
import { useAppDispatch } from "@store";
import { openDialog } from "@store/dialogs";
import {
  IconCheck,
  IconDeviceDesktop,
  IconDeviceMobile,
  IconDeviceTablet,
  IconX,
} from "@tabler/icons-react";
import moment from "moment";
import { useCallback } from "react";
import { AreYouSureDialog } from "../are-you-sure-dialog";
import { DeleteVersionLockDialog } from "../delete-version-lock-dialog";
import { SessionDetails } from "../session-details";
import { VersionsSessionRowActions } from "./versions-session-row-actions";

const DEVICE_ICONS = {
  Desktop: IconDeviceDesktop,
  Mobile: IconDeviceMobile,
  Tablet: IconDeviceTablet,
} as const;

export const VersionsTableSessionRow = ({
  session,
  pageTitle,
  versionTitle,
  storyId,
  versionId,
  allSessions,
  isOwner,
}: {
  session: ReaderType;
  pageTitle: string;
  versionTitle: string;
  storyId: string;
  versionId: string;
  allSessions?: ReaderType[];
  isOwner: boolean;
}) => {
  const DeviceIcon = DEVICE_ICONS[session.device as keyof typeof DEVICE_ICONS];
  const dispatch = useAppDispatch();

  const { isUserOrCreatorLite } = useAuthStoreShallow((s) => {
    return {
      isUserOrCreatorLite: s.isUserOrCreatorLite,
    };
  });

  const progressValue = (session.last_slide / session.slides_count) * 100;
  const progress = Number.isInteger(progressValue) ? progressValue : Number(progressValue.toFixed(1));

  const handleOpenSessionDetails = useCallback(() => {
    dispatch(
      openDialog({
        id: SessionDetails.id,
        content: SessionDetails,
        props: {
          session,
          pageTitle,
          versionTitle,
          storyId,
          versionId,
          allSessions,
          className: "tw-w-full",
        },
      }),
    );
  }, [
    dispatch,
    session,
    pageTitle,
    versionTitle,
    storyId,
    versionId,
    allSessions,
  ]);

  const handleDeleteSession = useCallback(() => {
    if (isUserOrCreatorLite && !isOwner) {
      dispatch(
        openDialog({
          id: DeleteVersionLockDialog.id,
          content: DeleteVersionLockDialog,
          props: {
            type: "session",
          },
        }),
      );
      return;
    }
    dispatch(
      openDialog({
        id: AreYouSureDialog.id,
        content: AreYouSureDialog,
        props: {
          sessionId: session.session_id,
        },
      }),
    );
  }, [dispatch, session.session_id, isUserOrCreatorLite, isOwner]);

  return (
    <div className="tw-flex tw-items-center tw-bg-[#F9F9FA] tw-border-b tw-border-solid tw-border-blue-100 tw-py-0.5 tw-min-h-[53px] hover:tw-bg-blue-50 tw-transition-colors last:tw-border-b-0 group/session-row">
      {/* Reader column */}
      <div className="tw-flex-auto tw-px-2 tw-pl-11 tw-py-2 tw-flex tw-items-center tw-min-w-0">
        <div className="tw-pl-2 tw-flex tw-flex-col tw-gap-1 tw-justify-center tw-min-w-0 tw-w-full">
          <div className="tw-flex tw-items-center tw-gap-1.5 tw-min-w-0">
            <p className="tw-font-sans tw-font-medium tw-text-sm tw-text-blue tw-truncate">
              {session.reader_name || `Reader #${session.visitor_index}`}
            </p>
            {!session.single_session_visitor && (
              <div className="tw-bg-blue-100 tw-rounded-full tw-px-1.5 tw-py-0.5 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                <p className="tw-font-sans tw-font-normal tw-text-xs tw-text-blue-500 tw-leading-4 tw-whitespace-nowrap">
                  Session #{session.session_index}
                </p>
              </div>
            )}
          </div>
          <ReaderMetaInfo
            position={session.reader_position}
            email={session.reader_email}
            customFields={session.reader_custom_fields}
          />
        </div>
      </div>

      {/* Session started column */}
      <div
        className="tw-px-2 tw-flex tw-items-center tw-h-[53px]"
        style={{ width: VERSIONS_TABLE_SESSIONS_SIZES.SESSION_STARTED }}
      >
        <p className="tw-font-sans tw-font-normal tw-text-sm tw-text-blue-500 tw-truncate">
          {moment.utc(session.timestamp).local().format("D MMM · h:mm A")}
        </p>
      </div>

      {/* Reading time column */}
      <div
        className="tw-px-2 tw-flex tw-items-center tw-h-[53px]"
        style={{ width: VERSIONS_TABLE_SESSIONS_SIZES.READING_TIME }}
      >
        <p className="tw-font-sans tw-font-normal tw-text-sm tw-text-blue-500 tw-truncate">
          {session.duration}
        </p>
      </div>

      {/* Clicked CTA column */}
      <div
        className="tw-px-2 tw-flex tw-items-center tw-h-[53px]"
        style={{ width: VERSIONS_TABLE_SESSIONS_SIZES.CLICKED_CTA }}
      >
        {session.click_cta ? (
          <IconCheck className="tw-size-6 tw-text-green-500" stroke={2} />
        ) : (
          <IconX className="tw-size-6 tw-text-blue-500" stroke={2} />
        )}
      </div>

      {/* Progress column */}
      <div
        className="tw-px-2 tw-flex tw-items-center tw-h-[53px]"
        style={{ width: VERSIONS_TABLE_SESSIONS_SIZES.PROGRESS }}
      >
        <div className="tw-flex tw-items-center tw-gap-2">
          <div className="tw-size-6 tw-flex tw-items-center tw-justify-center">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              className="tw-transform -tw-rotate-90"
              aria-label={`${progress}% progress`}
            >
              <title>{progress}% progress</title>
              <circle
                cx="12"
                cy="12"
                r="10"
                fill="none"
                stroke="#E7E6EC"
                strokeWidth="3"
              />
              {progress && (
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  fill="none"
                  stroke="#610CFF"
                  strokeWidth="3"
                  strokeDasharray={`${progress * 0.628} 62.8`}
                  strokeLinecap="round"
                />
              )}
            </svg>
          </div>
          <p className="tw-font-sans tw-font-normal tw-text-sm tw-text-blue-500 tw-whitespace-nowrap">
            {progress}%
          </p>
        </div>
      </div>

      {/* Location column */}
      <div
        className="tw-px-2 tw-flex tw-items-center tw-h-[53px]"
        style={{ width: VERSIONS_TABLE_SESSIONS_SIZES.LOCATION }}
      >
        <p className="tw-font-sans tw-font-normal tw-text-sm tw-text-blue-500 tw-truncate">
          {session.city ? `${session.city}, ` : ""}
          {session.country}
        </p>
      </div>

      {/* Device column with menu */}
      <div
        className="tw-px-2 tw-flex tw-items-center tw-justify-between tw-h-[53px]"
        style={{ width: VERSIONS_TABLE_SESSIONS_SIZES.DEVICE }}
      >
        <div className="tw-flex tw-items-center tw-gap-2.5 tw-min-w-0">
          <DeviceIcon
            className="tw-size-3.5 tw-text-blue-500 tw-flex-shrink-0"
            stroke={2}
          />
          <p className="tw-font-sans tw-font-normal tw-text-sm tw-text-blue-500 tw-truncate">
            {session.device}
          </p>
        </div>
        <VersionsSessionRowActions
          onSessionDetails={handleOpenSessionDetails}
          onDeleteSession={handleDeleteSession}
        />
      </div>
    </div>
  );
};
